import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { t } from "ttag";

import { login } from "@/app/api";
import { ROUTES } from "@/app/router";
import { Logo } from "@/assets";
import { trackUserLoggedIn } from "@/features/analytics/trackers";
import { FUNNEL_ROUTE } from "@/features/funnel/router/constants";
import { emailSchema, passwordSchema } from "@/shared/models";
import { authStore, useSettingsStore } from "@/shared/stores";
import {
  Alert,
  Button,
  Form,
  Heading,
  Input,
  LetterIcon,
  PasswordInput,
  Text,
  validateError,
} from "@/shared/ui";

// Maps a `?variant=` query param on this page to the onboarding funnel it
// should send new users into, so a landing page's "Create account" can send
// visitors back to the same funnel its own CTA points to.
const VARIANT_FUNNEL_ROUTES: Record<string, string> = {
  "alternative-landing": FUNNEL_ROUTE.MEASUREMENTS,
};

export const Login = () => {
  const setLanguage = useSettingsStore((state) => state.setLanguage);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const variant = searchParams.get("variant");
  const priceVariant = searchParams.get("price_variant");
  const funnelRoute =
    variant && Object.hasOwn(VARIANT_FUNNEL_ROUTES, variant)
      ? VARIANT_FUNNEL_ROUTES[variant]
      : undefined;
  const createAccountRoute = funnelRoute
    ? priceVariant
      ? `${funnelRoute}?price_variant=${priceVariant}`
      : funnelRoute
    : ROUTES.ONBOARDING.MAIN_INFO;

  const { mutate: loginMutation, isPending } = useMutation({
    mutationFn: login.mutation,
    onSuccess: (data, variables) => {
      authStore.setToken(data.accessToken);
      setLanguage(data.preferredLanguage);
      trackUserLoggedIn({ userEmail: variables.email });
      navigate(ROUTES.BASE);
    },
    onError: (error) => {
      const validatedError = validateError(error);

      if (validatedError) {
        setErrorMessage(validatedError.response.data.error.message);
      }
    },
  });

  const form = useForm({
    resolver: zodResolver(emailSchema.and(passwordSchema)),
  });

  const handleSubmit = form.handleSubmit((values) => loginMutation(values));

  return (
    <>
      <header className="flex w-full flex-col gap-16">
        <Logo className="h-8 sm:h-11" />

        <div className="flex flex-col items-center gap-2">
          <Heading size="medium">{t`Login`}</Heading>

          <Text align="center">
            {t`It's nice to see you again!`}
            <br />
            {t`Enter your login details below to continue.`}
          </Text>
        </div>
      </header>

      <Form.Provider {...form}>
        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-16">
          <div className="flex flex-col">
            <Form.Field
              control={form.control}
              name="email"
              render={() => (
                <Form.Item>
                  <Form.Label>{t`Email`}</Form.Label>
                  <Form.Control>
                    <Input.Root
                      {...form.register("email")}
                      autoComplete="email"
                      placeholder={t`email@example.com`}
                    >
                      <Input.Slot>
                        <LetterIcon />
                      </Input.Slot>
                    </Input.Root>
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="password"
              render={() => (
                <Form.Item>
                  <Form.Label>{t`Password`}</Form.Label>
                  <Form.Control>
                    <PasswordInput
                      {...form.register("password")}
                      autoComplete="current-password"
                      placeholder={t`your password here`}
                    />
                  </Form.Control>
                  <Form.Message compact={false} />

                  <Link
                    to={ROUTES.FORGOT_PASSWORD}
                    className="self-end text-sm font-medium text-neutral-950 underline underline-offset-2"
                  >
                    {t`Forgot your password?`}
                  </Link>
                </Form.Item>
              )}
            />
          </div>

          {errorMessage && <Alert type="error">{errorMessage}</Alert>}

          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isPending}
              onClick={() => setErrorMessage("")}
            >
              {t`Log In`}
            </Button>

            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate(createAccountRoute)}
              className="w-full"
            >
              {t`Create account`}
            </Button>
          </div>
        </form>
      </Form.Provider>
    </>
  );
};
